import { FileUploader } from "@/lib/FileUpload/FileUploader";
import { BlogHeroImageAltDialog } from "@/app/Blogs/Components/BlogHeroImageAltDialog";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { Button } from "@/components/catalyst/button";
import { PencilIcon, TrashIcon } from "lucide-react";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import {
  createMediaMutation,
  deleteMediaMutation,
} from "@/app/Media/GraphQL/mediaMutations";
import {
  MediaCollection,
  ModelType,
  type MediaFieldsFragment,
} from "@/gql/graphql";

type Props = {
  postId?: string;
  heroImage: MediaFieldsFragment | null;
  isEditable: boolean;
};

export function BlogHeroImagePanel({ postId, heroImage, isEditable }: Props) {
  const { mutateAsync: createMedia, isPending: isAttaching } =
    useGqlMutation(createMediaMutation);
  const { mutateAsync: deleteMedia, isPending: isDeleting } =
    useGqlMutation(deleteMediaMutation);
  const { open: openAltDialog } = useDialog(BlogHeroImageAltDialog);

  if (!postId) {
    return (
      <p className="text-sm text-zinc-500">
        Save the post first to upload a hero image.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {heroImage ? (
        <figure className="inline-block max-w-sm space-y-2">
          <div className="relative inline-block">
            <img
              src={heroImage.url}
              alt={heroImage.alt ?? ""}
              className="h-48 w-auto rounded-md border border-zinc-200 object-cover"
            />
            {isEditable ? (
              <div className="absolute right-2 top-2 flex gap-2">
                <Button
                  type="button"
                  color="white"
                  aria-label="Edit alt text"
                  LeadingIcon={PencilIcon}
                  onClick={() =>
                    openAltDialog({
                      mediaId: heroImage.id,
                      alt: heroImage.alt ?? null,
                      imageUrl: heroImage.url,
                    })
                  }
                />
                <Button
                  color="red"
                  type="button"
                  aria-label="Remove hero image"
                  isLoading={isDeleting}
                  LeadingIcon={TrashIcon}
                  onClick={async () => {
                    await deleteMedia({ id: heroImage.id });
                  }}
                />
              </div>
            ) : null}
          </div>
          <figcaption className="text-sm">
            {heroImage.alt ? (
              <span className="text-zinc-600">
                <span className="font-medium text-zinc-900">Alt text:</span>{" "}
                {heroImage.alt}
              </span>
            ) : (
              <span className="text-amber-700">
                No alt text yet. Search engines see the post title instead.
              </span>
            )}
          </figcaption>
        </figure>
      ) : null}

      <FileUploader
        compact
        isDisabled={!isEditable || isAttaching || isDeleting}
        maxFileCount={1}
        onUpload={async (uploads) => {
          const uploaded = uploads[0];
          if (!uploaded) return;
          if (heroImage) {
            await deleteMedia({ id: heroImage.id });
          }
          await createMedia({
            input: {
              file_name: uploaded.file.name,
              objectId: postId,
              objectType: ModelType.BlogPost,
              collectionName: MediaCollection.BlogHeroImage,
              key: uploaded.filePath,
            },
          });
        }}
      />
    </div>
  );
}
